import { useEffect, useMemo, useRef } from 'react';
import {
  BREAKDOWN_PANEL_CATEGORIES,
  ELEMENT_CATEGORIES,
  type ReviewSummary,
  type SceneIndexRow,
} from '@scoutaround/script-breakdown-shared';

import { Icon } from '../Icon.js';
import { INT_EXT_LABEL } from '../scenes/sceneLabels.js';
import { Disclosure } from '../ui/Disclosure.js';
import { MoreMenu, type MenuItem } from '../ui/MoreMenu.js';
import { SegmentBar, type Segment } from '../ui/SegmentBar.js';
import { SegmentedFilter } from '../ui/SegmentedFilter.js';
import { StatusDot } from '../ui/StatusPill.js';
import { WindowedList } from '../ui/WindowedList.js';
import type { ElementsAddress, ElementsNavigate, ProgressFilter, SceneSort } from './address.js';
import { CoverageMark } from './CoverageMark.js';
import { PROGRESS_LABEL, PROGRESS_STATE, itemsText, pageLength } from './labels.js';

export const SCENE_ROW_HEIGHT = 66;

/**
 * THE LEFT PANEL: WHERE THE WORK STANDS, AND WHICH SCENE IS OPEN.
 *
 * The bar and the filter counts are the server's summary from the same pass as the rows — never a
 * count of the rows drawn here (rule 1). Each row carries two marks that are never merged: the
 * dot is the HUMAN review progress, the ring is how much of the scene the AI reading examined.
 *
 * The search is the server's, over every scene's text (`/` focuses it); the other filters narrow
 * what the index already says about each scene. The list draws only what is in view, so a 300-
 * scene screenplay opens as fast as a 30-scene one.
 */
export function SceneListPanel({
  rows,
  allRows,
  summary,
  address,
  navigate,
  selectedId,
  onSelect,
  query,
  onQuery,
  searching,
  searchError,
  collapsed,
  onCollapsed,
  collapsible,
  menu,
}: {
  /** The rows to list: filtered, searched and sorted. */
  rows: readonly SceneIndexRow[];
  /** Every scene, for the filter options. */
  allRows: readonly SceneIndexRow[];
  summary: ReviewSummary;
  address: ElementsAddress;
  navigate: ElementsNavigate;
  selectedId: string | null;
  onSelect: (sceneId: string) => void;
  /** The search box's own text; the address takes it once typing pauses. */
  query: string;
  onQuery: (q: string) => void;
  searching: boolean;
  searchError: string | null;
  collapsed: boolean;
  onCollapsed: (collapsed: boolean) => void;
  /** Folding is offered where the list sits beside the script, not in a drawer or on a phone. */
  collapsible: boolean;
  /** More actions for the scenes shown (the multi-scene Key Takeaways). */
  menu: readonly MenuItem[];
}) {
  const search = useRef<HTMLInputElement>(null);

  // `/` puts the cursor in the search, wherever focus is — unless somebody is typing.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const typing =
        target !== null &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable);
      if (typing) return;
      event.preventDefault();
      if (collapsed) onCollapsed(false);
      window.setTimeout(() => {
        search.current?.focus();
      }, 0);
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [collapsed, onCollapsed]);

  const options = useMemo(() => {
    const categories = BREAKDOWN_PANEL_CATEGORIES.filter((c) => allRows.some((r) => (r.counts.byCategory[c] ?? 0) > 0));
    const distinct = (values: (string | null)[]) =>
      [...new Set(values.filter((v): v is string => v !== null && v.trim() !== ''))].sort((a, b) => a.localeCompare(b));

    return {
      categories,
      intExt: distinct(allRows.map((r) => r.intExt)),
      timeOfDay: distinct(allRows.map((r) => r.timeOfDay)),
      characters: distinct(allRows.flatMap((r) => r.castNames)),
    };
  }, [allRows]);

  const narrowed =
    address.cat !== null || address.ie !== null || address.tod !== null || address.char !== null || address.sort !== 'SCRIPT';

  if (collapsed) {
    return (
      <div className="el-list el-list-collapsed" data-testid="el-list">
        <button
          type="button"
          className="ctl el-list-expand"
          data-testid="el-list-expand"
          aria-label="Show the scene list"
          title="Show the scene list"
          onClick={() => {
            onCollapsed(false);
          }}
        >
          <Icon name="panelLeft" size={16} />
        </button>
        <span className="el-list-collapsed-count" aria-hidden="true">
          {`${String(summary.reviewed)}/${String(summary.scenes)}`}
        </span>
      </div>
    );
  }

  return (
    <div className="el-list" data-testid="el-list">
      <div className="el-list-head">
        <div className="el-list-title-row">
          <p className="el-list-title">Scenes &amp; workflow</p>
          <span className="el-list-title-tools">
            {menu.length === 0 ? null : <MoreMenu items={menu} label="More for the scenes shown" testId="el-list-more" />}
            {collapsible ? (
              <button
                type="button"
                className="ctl el-icon-button"
                data-testid="el-list-collapse"
                aria-label="Fold the scene list"
                title="Fold the scene list"
                onClick={() => {
                  onCollapsed(true);
                }}
              >
                <Icon name="chevronLeft" size={15} />
              </button>
            ) : null}
          </span>
        </div>

        <SegmentBar
          testId="el-summary"
          label={`${String(summary.reviewed)} of ${String(summary.scenes)} scenes reviewed`}
          segments={(
            [
            { id: 'reviewed', label: 'reviewed', value: summary.reviewed, tone: 'ok' },
            { id: 'in-progress', label: 'in progress', value: summary.inProgress, tone: 'ink' },
            { id: 're-review', label: 'need re-review', value: summary.needsReReview, tone: 'danger' },
            { id: 'reopened', label: 'reopened', value: summary.reopened, tone: 'warn' },
            { id: 'not-started', label: 'not started', value: summary.notStarted, tone: 'muted' },
            ] satisfies Segment[]
          ).filter((s) => s.value > 0 || s.id === 'reviewed' || s.id === 'not-started')}
        />
        {summary.notFullyRead > 0 ? (
          <p className="el-list-note" data-testid="el-summary-not-fully-read">
            <span className="el-coverage" data-tone="warn" aria-hidden="true">
              <svg width="12" height="12" viewBox="0 0 16 16">
                <circle cx="8" cy="8" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                <path d="M8 2.5a5.5 5.5 0 0 1 0 11z" fill="currentColor" />
              </svg>
            </span>
            {`${String(summary.notFullyRead)} ${summary.notFullyRead === 1 ? 'scene was' : 'scenes were'} not fully read by the AI`}
          </p>
        ) : null}

        <label className="ui-search el-search">
          <Icon name="search" size={15} />
          <input
            ref={search}
            type="search"
            value={query}
            placeholder="Search scenes and text"
            aria-label="Search scenes by number, heading or text"
            aria-keyshortcuts="/"
            data-testid="el-search"
            onChange={(event) => {
              onQuery(event.target.value);
            }}
          />
          <kbd className="el-kbd" aria-hidden="true">
            /
          </kbd>
        </label>
        {searchError === null ? null : (
          <p className="el-list-note el-list-error" role="alert" data-testid="el-search-error">
            {searchError}
          </p>
        )}

        <SegmentedFilter<ProgressFilter>
          label="Show scenes"
          testIdPrefix="el-filter"
          value={address.filter}
          onChange={(filter) => {
            navigate({ filter });
          }}
          options={[
            { id: 'ALL', label: 'All', count: summary.scenes },
            { id: 'TO_REVIEW', label: 'To review', count: summary.scenes - summary.reviewed },
            ...(summary.needsReReview > 0 || address.filter === 'NEEDS_RE_REVIEW'
              ? [{ id: 'NEEDS_RE_REVIEW' as const, label: 'Re-review', count: summary.needsReReview, tone: 'attention' as const }]
              : []),
            { id: 'REVIEWED', label: 'Reviewed', count: summary.reviewed },
          ]}
        />

        <Disclosure
          summary="More filters"
          meta={narrowed ? 'On' : undefined}
          testId="el-more-filters"
          defaultOpen={narrowed}
        >
          <div className="el-filters">
            <FilterSelect
              id="cat"
              label="Category"
              value={address.cat}
              options={options.categories.map((c) => ({ value: c, label: ELEMENT_CATEGORIES[c].label }))}
              onChange={(value) => {
                navigate({ cat: value as typeof address.cat });
              }}
            />
            <FilterSelect
              id="ie"
              label="Int. / Ext."
              value={address.ie}
              options={options.intExt.map((v) => ({ value: v, label: INT_EXT_LABEL[v] ?? v }))}
              onChange={(value) => {
                navigate({ ie: value });
              }}
            />
            <FilterSelect
              id="tod"
              label="Time of day"
              value={address.tod}
              options={options.timeOfDay.map((v) => ({ value: v, label: v }))}
              onChange={(value) => {
                navigate({ tod: value });
              }}
            />
            <FilterSelect
              id="char"
              label="Character"
              value={address.char}
              options={options.characters.map((v) => ({ value: v, label: v }))}
              onChange={(value) => {
                navigate({ char: value });
              }}
            />
            <label className="el-filter-field">
              <span>Sort</span>
              <select
                value={address.sort}
                data-testid="el-sort"
                onChange={(event) => {
                  navigate({ sort: event.target.value as SceneSort });
                }}
              >
                <option value="SCRIPT">Script order</option>
                <option value="LOCATION">Location</option>
                <option value="STATUS">Review status</option>
              </select>
            </label>
            {narrowed ? (
              <button
                type="button"
                className="el-link"
                data-testid="el-filters-clear"
                onClick={() => {
                  navigate({ cat: null, ie: null, tod: null, char: null, sort: 'SCRIPT' });
                }}
              >
                Clear these filters
              </button>
            ) : null}
          </div>
        </Disclosure>

        <p className="el-list-count" aria-live="polite" data-testid="el-list-count">
          {searching
            ? 'Searching the script…'
            : rows.length === allRows.length
              ? `${String(allRows.length)} ${allRows.length === 1 ? 'scene' : 'scenes'}`
              : `Showing ${String(rows.length)} of ${String(allRows.length)} scenes`}
        </p>
      </div>

      <WindowedList
        items={rows}
        getKey={(row) => row.id}
        rowHeight={SCENE_ROW_HEIGHT}
        selectedKey={selectedId}
        onSelect={onSelect}
        label="Scenes"
        testId="el-scene-list"
        rowTestId={(row) => `el-scene-${row.id}`}
        empty={
          <p data-testid="el-scene-list-empty">
            No scene matches.{' '}
            <button
              type="button"
              className="el-link"
              onClick={() => {
                onQuery('');
                navigate({ q: '', filter: 'ALL', cat: null, ie: null, tod: null, char: null });
              }}
            >
              Show every scene
            </button>
          </p>
        }
        renderRow={(row, { selected }) => <SceneRow row={row} selected={selected} />}
      />
    </div>
  );
}

function SceneRow({ row, selected }: { row: SceneIndexRow; selected: boolean }) {
  const length = pageLength(row.pageEighths);
  const progress = row.progress;

  return (
    <div className="el-row" data-selected={selected ? '1' : '0'} data-progress={progress}>
      <span className="el-row-num">{row.sceneNumber ?? String(row.ordinal)}</span>
      <span className="el-row-main">
        <span className="el-row-title">{row.slugline ?? 'Untitled scene'}</span>
        <span className="el-row-sub">
          {[length, itemsText(row.counts.items)].filter((part): part is string => part !== null).join(' · ')}
          {row.counts.awaiting > 0 ? (
            <span className="el-row-awaiting" data-testid={`el-scene-awaiting-${row.id}`}>{` · ${String(row.counts.awaiting)} to decide`}</span>
          ) : null}
        </span>
      </span>
      <span className="el-row-end">
        <StatusDot state={PROGRESS_STATE[progress]} label={PROGRESS_LABEL[progress]} testId={`el-scene-progress-${row.id}`} />
        <CoverageMark coverage={row.coverage} testId={`el-scene-coverage-${row.id}`} />
      </span>
    </div>
  );
}

function FilterSelect({
  id,
  label,
  value,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string | null;
  options: readonly { value: string; label: string }[];
  onChange: (value: string | null) => void;
}) {
  if (options.length === 0 && value === null) return null;

  return (
    <label className="el-filter-field">
      <span>{label}</span>
      <select
        value={value ?? ''}
        data-testid={`el-filter-${id}`}
        onChange={(event) => {
          onChange(event.target.value === '' ? null : event.target.value);
        }}
      >
        <option value="">Any</option>
        {value !== null && !options.some((o) => o.value === value) ? <option value={value}>{value}</option> : null}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
